/**
 * Phase 4 slice 4 — CAPA Effectiveness. Recurrence detection: a CAPA is
 * "ineffective" if a same-category, same-location NCR recurs within 90 days of
 * its closure. The ISO audit metric that matters.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ShieldAlert } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';

import { formatDate as fmt } from '../../lib/format';

export default function CAPAEffectiveness() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => { apiGet('/projects').then(setProjects).catch(() => toast.error('Failed to load projects')); }, []);
  useEffect(() => { apiGet(`/quality/capa-effectiveness${projectId ? `?project_id=${projectId}` : ''}`).then(setData).catch(() => {}); }, [projectId]);

  const pct = data && data.evaluated ? Math.round((data.effective / data.evaluated) * 100) : null;

  return (
    <div>
      <PageHeader overline="Quality" title="CAPA Effectiveness" subtitle="Did our corrective actions actually work? Recurrence within 90 days = ineffective" />
      <div className="mb-4">
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[260px]" data-testid="capa-project-select">
          <option value="">All projects</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5" data-testid="capa-summary">
        <div className="bg-white border border-slate-200 rounded-sm p-4"><div className="kpi-overline">CAPAs Evaluated</div><div className="text-3xl font-display font-extrabold text-slate-800 mt-1">{data?.evaluated ?? 0}</div></div>
        <div className="bg-white border border-emerald-200 rounded-sm p-4"><div className="kpi-overline text-emerald-700 flex items-center gap-1"><ShieldCheck size={12} /> Effective</div><div className="text-3xl font-display font-extrabold text-emerald-600 mt-1">{data?.effective ?? 0}</div></div>
        <div className="bg-white border border-red-200 rounded-sm p-4"><div className="kpi-overline text-red-700 flex items-center gap-1"><ShieldAlert size={12} /> Ineffective</div><div className="text-3xl font-display font-extrabold text-red-600 mt-1">{data?.ineffective ?? 0}</div></div>
        <div className="bg-white border border-slate-200 rounded-sm p-4"><div className="kpi-overline">Effectiveness Rate</div><div className="text-3xl font-display font-extrabold text-slate-800 mt-1">{pct != null ? `${pct}%` : '—'}</div></div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full dense-table" data-testid="capa-table">
            <thead><tr><th>NCR No.</th><th>Category</th><th>Location</th><th>Closed</th><th className="text-center">Recurrences (90d)</th><th>Effectiveness</th></tr></thead>
            <tbody>
              {!data || data.capas.length === 0 ? <tr><td colSpan={6} className="text-center text-sm text-slate-400 py-10">No closed CAPAs to evaluate yet.</td></tr>
                : data.capas.map((c) => (
                  <tr key={c.capa_id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/app/quality/ncrs/${c.original_ncr_id}`)} data-testid={`capa-row-${c.capa_id}`}>
                    <td className="text-xs font-display font-bold text-[#3b4872]">{c.ncr_no}</td>
                    <td className="text-xs text-slate-600">{c.category}</td>
                    <td className="text-[13px] max-w-[200px] truncate">{c.location_on_site || '—'}</td>
                    <td className="text-xs text-slate-500">{fmt(c.closed_at)}</td>
                    <td className="text-center font-display font-bold">{c.recurrence_count}</td>
                    <td><span className={`tag border ${c.effectiveness === 'effective' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>{c.effectiveness}</span></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
